// Движковый слой экрана разбора: ленивый WASM, прогрессивный рендер quick/deep
// прохода, кэш в localStorage, on-demand анализ текущей позиции (приоритет),
// стрелка лучшего хода и топ-2 линии. Отделён от game.ts, чтобы держать оба
// файла под лимитом 400 строк.
import type { MiniBoard } from '@ext/panel/board/mini-board';
import type { GameNode } from '@ext/core/types';
import type { EngineAnalysis } from '@ext/core/eval-utils';
import { pvToSan, uciToMove } from '@ext/core/eval-utils';
import { setEngineArtifacts } from '@ext/core/evals';
import { t } from '../i18n';
import { refreshEngineData, type ReviewModel } from '../core/review-model';
import { PriorityScheduler, runReview, ON_DEMAND, ENGINE_DEPTH, type LowLevelAnalyze } from '../services/analysis';
import type { AnalyzeOptions } from '@ext/engine/engine';
import { analyze as loaderAnalyze, engineHealthy } from '../services/engine-loader';
import { loadEvals, saveEvals } from '../services/eval-cache';

const PV_PLIES = 6; // сколько полуходов линии показываем

export interface EngineHooks {
  model: ReviewModel;
  board: MiniBoard;
  gameId: string;
  linesEl: HTMLElement;
  progressEl: HTMLElement | null;
  progressLabel: HTMLElement | null;
  progressBar: HTMLElement | null;
  bestToggle: HTMLInputElement | null;
  /** Перерисовать head/график/чипы/кадр после пополнения модели. */
  onRefresh(): void;
}

function fmtLineEval(cp: number | null, mate: number | null): string {
  if (mate != null) return mate > 0 ? `#${mate}` : `#-${-mate}`;
  if (cp == null) return '';
  const p = cp / 100;
  const sign = p > 0 ? '+' : p < 0 ? '−' : '';
  return `${sign}${Math.abs(p).toFixed(1)}`;
}

export class EngineController {
  private sched: PriorityScheduler;
  private lastAnalysis = new Map<string, EngineAnalysis>();
  private showBest = true;
  private alive = true;
  private curNode: GameNode | null = null;
  private progressCounter = 0;
  private analyzedListener: ((node: GameNode) => void) | null = null;

  constructor(private hooks: EngineHooks, lowLevel: LowLevelAnalyze = loaderAnalyze) {
    this.sched = new PriorityScheduler(lowLevel);
    const toggle = hooks.bestToggle;
    if (toggle) {
      this.showBest = toggle.checked;
      toggle.addEventListener('change', () => {
        this.showBest = toggle.checked;
        if (this.curNode) this.paintArrow(this.curNode);
      });
    }
  }

  /** Persistent-тумблер 💡: показать/снять стрелку лучшего хода (источник — артефакты/движок). */
  setShowBest(on: boolean): void {
    this.showBest = on;
    if (this.curNode) this.paintArrow(this.curNode);
  }

  destroy(): void {
    this.alive = false;
  }

  /** Загрузить кэш и (для партий без [%eval]) запустить фоновый разбор. */
  async start(willAnalyze: boolean): Promise<void> {
    const { model, gameId } = this.hooks;
    const cached = loadEvals(gameId, model.root, model.evals);
    if (cached.found) {
      refreshEngineData(model);
      this.hooks.onRefresh();
    }
    if (!willAnalyze || cached.complete) return;

    // Первое открытие WASM: движок (~7МБ) грузится лениво — показываем лоадер,
    // пока engineHealthy() поднимает Stockfish, иначе экран «висит» без обратной связи.
    this.showProgress();
    this.setProgressText(t('game.loading_engine'));
    const healthy = await engineHealthy();
    if (!this.alive) return;
    if (!healthy) {
      this.setProgressText(t('game.engine_failed'));
      return;
    }
    try {
      await runReview(this.sched, model.root, model.evals, {
        isCurrent: () => this.alive,
        onProgress: (p) => {
          this.setProgressText(t('game.analyzing', { done: p.done, total: p.total }));
          this.setProgressBar(p.done, p.total);
          this.progressCounter++;
          if (p.done === p.total || this.progressCounter % 5 === 0) {
            refreshEngineData(model);
            this.hooks.onRefresh();
          }
        },
      });
    } catch {
      /* прерван/сбой движка — оставляем что успели */
    }
    if (!this.alive) return;
    refreshEngineData(model);
    this.hooks.onRefresh();
    saveEvals(gameId, model.root, model.evals, true);
    this.hideProgress();
  }

  /** Подписка на завершение анализа узла (для карточки режима «свой вариант»). */
  onAnalyzed(fn: (node: GameNode) => void): void {
    this.analyzedListener = fn;
  }

  /** On-demand анализ произвольной позиции через общий планировщик (режим «игра с движком»). */
  analyzePriority(fen: string, opts: AnalyzeOptions = ON_DEMAND): Promise<EngineAnalysis> {
    return this.sched.requestPriority(fen, opts);
  }

  /**
   * Оценить позицию узла ВНЕ главной линии (режим «свой вариант»): стрелка,
   * топ-линии и on-demand анализ — но БЕЗ refreshEngineData/onRefresh, чтобы не
   * трогать модель главной партии (ленту/график/счётчики).
   */
  analyzeVariation(node: GameNode): void {
    this.curNode = node;
    this.paintArrow(node);
    const cached = this.lastAnalysis.get(node.fen);
    if (cached) {
      this.renderLines(node, cached);
      this.analyzedListener?.(node);
      return;
    }
    this.renderLinesFromArtifacts(node);
    this.sched.requestPriority(node.fen, ON_DEMAND).then(
      (a) => {
        this.lastAnalysis.set(node.fen, a);
        setEngineArtifacts(node, a, ENGINE_DEPTH);
        if (!this.alive || this.curNode !== node) return;
        this.paintArrow(node);
        this.renderLines(node, a);
        this.analyzedListener?.(node);
      },
      () => {
        /* superseded/сбой — молча */
      },
    );
  }

  /** Показать стрелку/линии для позиции текущего кадра (node = позиция на доске). */
  showFor(node: GameNode): void {
    this.curNode = node;
    this.paintArrow(node);
    const cached = this.lastAnalysis.get(node.fen);
    if (cached) {
      this.renderLines(node, cached);
      return;
    }
    this.renderLinesFromArtifacts(node);
    this.requestOnDemand(node);
  }

  private requestOnDemand(node: GameNode): void {
    this.sched.requestPriority(node.fen, ON_DEMAND).then(
      (a) => {
        this.lastAnalysis.set(node.fen, a);
        this.hooks.model.evals.cacheAnalysis(node.fen, a);
        setEngineArtifacts(node, a, ENGINE_DEPTH);
        if (!this.alive) return;
        if (this.curNode === node) {
          this.paintArrow(node);
          this.renderLines(node, a);
        }
        refreshEngineData(this.hooks.model);
        this.hooks.onRefresh();
      },
      () => {
        /* superseded/сбой — молча */
      },
    );
  }

  private paintArrow(node: GameNode): void {
    const uci = node._bestmove;
    if (!this.showBest || !uci) {
      this.hooks.board.setArrow(null);
      return;
    }
    const m = uciToMove(uci);
    this.hooks.board.setArrow(m ? { from: m.from, to: m.to } : null);
  }

  private renderLinesFromArtifacts(node: GameNode): void {
    if (!node._pv || !node._pv.length) {
      this.hooks.linesEl.innerHTML = '';
      return;
    }
    const san = pvToSan(node.fen, node._pv.slice(0, PV_PLIES));
    this.hooks.linesEl.innerHTML = this.lineRow(fmtLineEval(node._bestCp ?? null, node._mateWhite ?? null), san);
  }

  private renderLines(node: GameNode, a: EngineAnalysis): void {
    const lines = (a.lines && a.lines.length ? a.lines : [{ cp: a.cp, mate: a.mate, pv: a.pv, move: a.bestmove }]).slice(0, 2);
    this.hooks.linesEl.innerHTML = lines
      .map((l) => this.lineRow(fmtLineEval(l.cp, l.mate), pvToSan(node.fen, (l.pv || []).slice(0, PV_PLIES))))
      .filter(Boolean)
      .join('');
  }

  private lineRow(evalStr: string, san: string): string {
    if (!san) return '';
    return `<div class="engine-line"><span class="mono line-eval">${evalStr}</span><span class="line-san">${san}</span></div>`;
  }

  private showProgress(): void {
    this.hooks.progressEl?.classList.remove('hidden');
  }

  private hideProgress(): void {
    this.hooks.progressEl?.classList.add('hidden');
  }

  private setProgressText(text: string): void {
    if (this.hooks.progressLabel) this.hooks.progressLabel.textContent = text;
  }

  private setProgressBar(done: number, total: number): void {
    if (this.hooks.progressBar) {
      const pct = total ? Math.round((done / total) * 100) : 0;
      this.hooks.progressBar.style.setProperty('--value', `${pct}%`);
    }
  }
}
